<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useMessageContext } from '../provider.js';
import { MESSAGE_VARIANTS } from '../constants';
import FormattedContent from './Text/FormattedContent.vue';

const props = defineProps({
  attachmentId: {
    type: [Number, String],
    required: true,
  },
});

const { contentAttributes, variant } = useMessageContext();
const { t } = useI18n();
const isExpanded = ref(false);

const COLLAPSED_CONTENT_CHARACTERS = 2000;

const isBrandSurface = computed(() =>
  [MESSAGE_VARIANTS.AGENT, MESSAGE_VARIANTS.TEMPLATE].includes(variant.value)
);

const surfaceClass = computed(() =>
  isBrandSurface.value
    ? 'border-white/15 bg-white/5 text-white/90'
    : 'border-n-weak/40 bg-n-alpha-2'
);

const dividerClass = computed(() =>
  isBrandSurface.value ? 'border-white/15 text-white/80' : 'border-n-weak/40'
);

const iconClass = computed(() =>
  isBrandSurface.value ? 'text-white/90' : 'text-n-brand'
);

const formattedContentClass = computed(() =>
  isBrandSurface.value
    ? '[&_.prose-bubble_*]:!text-white/90 [&_.prose-bubble_td]:!border-white/20 [&_.prose-bubble_th]:!border-white/20 [&_.prose-bubble_th]:!bg-white/5'
    : ''
);

const mediaContent = computed(() => {
  const contents =
    contentAttributes.value?.cortexNormalizedContent?.mediaContents || [];
  return contents.find(
    item => Number(item.attachmentId) === Number(props.attachmentId)
  );
});

const displayText = computed(() => {
  const text = mediaContent.value?.text?.toString() || '';
  if (mediaContent.value?.mediaType !== 'document') return text;

  return text.replace(/(\|\s*)field[ _]value:\s*/gi, '$1');
});

const videoContentSegments = computed(() => {
  if (mediaContent.value?.mediaType !== 'video') return [];

  const segments = [];
  let contentLines = [];

  const appendContent = () => {
    const content = contentLines.join('\n').trim();
    if (content) segments.push({ type: 'content', content });
    contentLines = [];
  };

  const lines = displayText.value.split(/\r?\n/);
  lines.forEach(line => {
    const indicator = line.match(/^\s*(time|event|spoken)\s*:\s*(.*)$/i);

    if (!indicator) {
      contentLines.push(line);
      return;
    }

    appendContent();
    segments.push({
      type: indicator[1].toLowerCase(),
      content: indicator[2],
    });
  });

  appendContent();
  return segments.map((segment, index) => ({ ...segment, id: index }));
});

const hasVideoIndicators = computed(() =>
  videoContentSegments.value.some(segment => segment.type !== 'content')
);

const videoIndicatorIcon = type =>
  ({
    time: 'i-lucide-clock',
    event: 'i-lucide-flag',
    spoken: 'i-lucide-audio-lines',
  })[type];

const videoIndicatorLabel = type =>
  ({
    time: t('CORTEX_MEDIA_CONTENT.INDICATORS.TIME'),
    event: t('CORTEX_MEDIA_CONTENT.INDICATORS.EVENT'),
    spoken: t('CORTEX_MEDIA_CONTENT.INDICATORS.SPOKEN'),
  })[type];

const isCollapsible = computed(
  () => displayText.value.length > COLLAPSED_CONTENT_CHARACTERS
);

const expansionLabel = computed(() => {
  if (isExpanded.value) {
    return t('CORTEX_MEDIA_CONTENT.ACTIONS.SHOW_LESS');
  }

  return t('CORTEX_MEDIA_CONTENT.ACTIONS.SHOW_MORE');
});

const icon = computed(() => {
  return (
    {
      audio: 'i-lucide-audio-lines',
      image: 'i-lucide-scan-text',
      video: 'i-lucide-clapperboard',
      document: 'i-lucide-file-text',
      text: 'i-lucide-file-text',
    }[mediaContent.value?.mediaType] || 'i-lucide-scan-text'
  );
});

const label = computed(() => {
  switch (mediaContent.value?.mediaType) {
    case 'audio':
      return t('CORTEX_MEDIA_CONTENT.LABELS.AUDIO');
    case 'image':
      return t('CORTEX_MEDIA_CONTENT.LABELS.IMAGE');
    case 'video':
      return t('CORTEX_MEDIA_CONTENT.LABELS.VIDEO');
    case 'text':
      return t('CORTEX_MEDIA_CONTENT.LABELS.TEXT');
    default:
      return t('CORTEX_MEDIA_CONTENT.LABELS.DOCUMENT');
  }
});

const providerName = responsible => {
  if (responsible?.model?.toLowerCase().startsWith('gemini')) return 'gemini';

  return responsible?.provider;
};

const localResponsibleName = computed(() => {
  const responsible = mediaContent.value?.responsible;
  if (!responsible) return '';

  if (responsible.source === 'local_model') {
    return [responsible.engine, responsible.model].filter(Boolean).join(' · ');
  }

  if (responsible.source === 'hybrid') {
    return [responsible.engine, responsible.localModel]
      .filter(Boolean)
      .join(' · ');
  }

  if (responsible.source === 'configured_model') return '';

  return responsible.engine;
});

const refinementResponsibleName = computed(() => {
  const responsible = mediaContent.value?.responsible;
  if (!responsible) return '';

  if (
    responsible.source === 'hybrid' ||
    responsible.source === 'configured_model'
  ) {
    return [providerName(responsible), responsible.model]
      .filter(Boolean)
      .join(' · ');
  }

  return '';
});

const localResponsibleLabel = computed(() => {
  if (!localResponsibleName.value) return '';

  if (mediaContent.value?.mediaType === 'audio') {
    return t('CORTEX_MEDIA_CONTENT.RESPONSIBLE.TRANSCRIBED_BY', {
      responsible: localResponsibleName.value,
    });
  }

  return t('CORTEX_MEDIA_CONTENT.RESPONSIBLE.CONVERTED_BY', {
    responsible: localResponsibleName.value,
  });
});

const refinementResponsibleLabel = computed(() => {
  if (!refinementResponsibleName.value) return '';

  return t('CORTEX_MEDIA_CONTENT.RESPONSIBLE.REFINED_BY', {
    responsible: refinementResponsibleName.value,
  });
});

const responsibleLines = computed(() =>
  [localResponsibleLabel.value, refinementResponsibleLabel.value].filter(
    Boolean
  )
);
</script>

<template>
  <div
    v-show="mediaContent?.text"
    class="rounded-lg border px-3 py-2.5"
    :class="surfaceClass"
    data-testid="cortex-media-content"
  >
    <div class="mb-1.5 flex items-center gap-1.5 text-xs font-medium">
      <span class="size-3.5" :class="[icon, iconClass]" />
      {{ label }}
    </div>
    <div
      :class="{ 'max-h-96 overflow-hidden': isCollapsible && !isExpanded }"
      data-testid="cortex-media-body"
    >
      <div
        v-if="hasVideoIndicators"
        class="flex flex-col gap-1"
        data-testid="cortex-video-content"
      >
        <template v-for="segment in videoContentSegments" :key="segment.id">
          <FormattedContent
            v-if="segment.type === 'content'"
            :content="segment.content"
            spaced-line-breaks
            class="text-sm leading-5 [&_.prose-bubble_a]:!text-current [&_.prose-bubble_a]:opacity-80 [&_.prose-bubble_a]:underline [&_.prose-bubble_a]:decoration-current/60 [&_.prose-bubble_a]:underline-offset-2 hover:[&_.prose-bubble_a]:opacity-90 hover:[&_.prose-bubble_a]:decoration-current/80 focus-within:[&_.prose-bubble_a]:opacity-90 focus-within:[&_.prose-bubble_a]:decoration-current"
            :class="formattedContentClass"
          />
          <div
            v-else
            class="flex min-w-0 items-start gap-1.5 text-sm leading-5"
            :data-testid="`cortex-video-${segment.type}`"
          >
            <span
              class="mt-0.5 size-3.5 shrink-0"
              :class="[videoIndicatorIcon(segment.type), iconClass]"
              aria-hidden="true"
            />
            <span class="sr-only">{{ videoIndicatorLabel(segment.type) }}</span>
            <span class="min-w-0 flex-1">{{ segment.content }}</span>
          </div>
        </template>
      </div>
      <FormattedContent
        v-else-if="displayText"
        :content="displayText"
        spaced-line-breaks
        class="text-sm leading-5 [&_.prose-bubble_a]:!text-current [&_.prose-bubble_a]:opacity-80 [&_.prose-bubble_a]:underline [&_.prose-bubble_a]:decoration-current/60 [&_.prose-bubble_a]:underline-offset-2 hover:[&_.prose-bubble_a]:opacity-90 hover:[&_.prose-bubble_a]:decoration-current/80 focus-within:[&_.prose-bubble_a]:opacity-90 focus-within:[&_.prose-bubble_a]:decoration-current"
        :class="formattedContentClass"
        data-testid="cortex-media-markdown"
      />
    </div>
    <NextButton
      v-if="isCollapsible"
      type="button"
      variant="link"
      color="slate"
      size="xs"
      :label="expansionLabel"
      :icon="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
      class="mt-2 !text-current"
      :aria-expanded="isExpanded"
      data-testid="cortex-media-expansion"
      @click="isExpanded = !isExpanded"
    />
    <div
      v-if="responsibleLines.length"
      class="mt-2 flex flex-col gap-1 border-t pt-2 text-xs"
      :class="dividerClass"
      data-testid="cortex-media-responsible"
    >
      <div
        v-for="line in responsibleLines"
        :key="line"
        class="flex items-center gap-1.5"
      >
        <span class="i-lucide-badge-check size-3.5 shrink-0" />
        {{ line }}
      </div>
    </div>
  </div>
</template>
